import "./customAreaChart.scss";
import React, {useEffect, useMemo, useState, useContext} from "react";
import {
    AreaChart,
    Area,
    XAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    YAxis,
    LabelList,
    Legend,
} from "recharts";
import {
    Card,
    Button,
    Space,
    Typography,
    Tooltip as AntTooltip,
    Divider,
    Switch
} from "antd";
import {
    DownloadOutlined,
    BarChartOutlined,
    ReloadOutlined,
    EyeOutlined,
    EyeInvisibleOutlined
} from "@ant-design/icons";
import {OtherApi} from "../../../common/axios";
import dayjs from "dayjs";
import {CSVLink} from "react-csv";
import {DarkModeContext} from "../../../shared/contexts/darkModeContext";
import {useChartStatistics} from "../useSatistics";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";

const {Title, Text} = Typography;
const LIMIT_LABELS = 30; // 라벨 표시 제한
const DEFAULT_EXCHANGE_RATE = 1350; // 기본 환율(예시)
// ccu 레벨 그룹 (서버 level_group 1~6). 앞에 있을수록 차트 하단에 쌓임
const CCU_LEVEL_GROUPS = [
    {key: 'g1', name: '1~50Lv', color: '#cfe9cf'},
    {key: 'g2', name: '51~100Lv', color: '#a8d8b0'},
    {key: 'g3', name: '101~150Lv', color: '#86cbb4'},
    {key: 'g4', name: '151~200Lv', color: '#63bfd0'},
    {key: 'g5', name: '201~300Lv', color: '#3fa9e6'},
    {key: 'g6', name: '301Lv 이상', color: '#1890ff'},
];
const CCU_TOTAL = {key: 'Total', name: '총계', color: '#10306b'};
// 토글/툴팁 표시 순서: 차트에 쌓인 순서대로 위에서 아래로 (총계 > 고레벨 > 저레벨)
const CCU_TOGGLES = [CCU_TOTAL, ...[...CCU_LEVEL_GROUPS].reverse()];
const CustomAreaChart = ({
                             title,
                             type,
                             dateRange,
                             isSum = false,
                             height = 400
                         }) => {
    const {darkMode} = useContext(DarkModeContext);
    const {data, isLoading, error, refetch} = useChartStatistics(type, dateRange);
    const [showLabels, setShowLabels] = useState(true); // 라벨 표시 여부
    const isCcu = type === 'ccu';
    // ccu 총계 선은 최상위 레벨 구간(파랑)과 구분되게 남색, 다크모드에서는 밝은 색
    const totalColor = isCcu ? (darkMode ? '#e6f4ff' : CCU_TOTAL.color) : '#1890ff';
    const [hiddenKeys, setHiddenKeys] = useState([]); // 토글로 숨긴 영역
    const toggleHidden = (dataKey) => {
        setHiddenKeys(prev => prev.includes(dataKey) ? prev.filter(key => key !== dataKey) : [...prev, dataKey]);
    };

    // ccu 레벨 구간 on/off 토글 (차트 우측)
    const renderCcuToggles = () => (
        <div style={{paddingLeft: 16}}>
            {CCU_TOGGLES.map(({key, name, color}) => (
                <div key={key} style={{marginBottom: 6, whiteSpace: 'nowrap'}}>
                    <Switch
                        size="small"
                        checked={!hiddenKeys.includes(key)}
                        onChange={() => toggleHidden(key)}
                        style={{backgroundColor: hiddenKeys.includes(key) ? undefined : color}}
                    />
                    <Text style={{marginLeft: 8, fontSize: 12}}>{name}</Text>
                </div>
            ))}
        </div>
    );
    // 통화 관련 상태
    const [currency, setCurrency] = useState('USD'); // 'USD' | 'KRW'
    const [exchangeRate, setExchangeRate] = useState(DEFAULT_EXCHANGE_RATE);
    const getExchangeRate = async () => {
        try {
            const exchangeResult = await OtherApi.get('https://m.search.naver.com/p/csearch/content/qapirender.nhn?key=calculator&pkid=141&q=%ED%99%98%EC%9C%A8&where=m&u1=keb&u6=standardUnit&u7=0&u3=USD&u4=KRW&u8=down&u2=1');
            const valueStr = exchangeResult.data.country[1].value;
            setExchangeRate(parseFloat(valueStr.replace(/,/g, "")));
        } catch (e) {
            console.error(e);
        }
    }

    // 환율 정보 가져오기 (실제 환율 API 연동 가능, 여기선 고정값)
    useEffect(() => {
        if (type === 'revenue') {
            getExchangeRate();
        }
    }, [type]);

    // 통화 변환 함수
    const convertValue = (value) => {
        if (currency === 'KRW') {
            return Math.round(value * exchangeRate);
        }
        return value;
    };

    // processedData에 통화 변환 적용
    const processedData = useMemo(() => {
        let accumulatedTotal = 0;
        if (type === 'userLevel') {
            if(!data || data.length === 0) return [];
            return data[0]?.map(({lv, user_count}) => {
                return {
                    name: lv,
                    Total: user_count
                }
            });
        } else {
            return data?.map(item => {
                const total = type === 'revenue' ? convertValue(item.Total) : item.Total;
                accumulatedTotal += total;
                return {
                    ...item,
                    // SUM 결과가 문자열로 올 수 있어서 숫자로 변환
                    ...(type === 'ccu' && Object.fromEntries(CCU_LEVEL_GROUPS.map(({key}) => [key, Number(item[key] ?? 0)]))),
                    Total: total,
                    accumulatedTotal: accumulatedTotal
                };
            });
        }
    }, [data, type, currency]);

    const csvData = useMemo(() => {
        return processedData?.map(item => ({
            '날짜': item.keyName,
            '총계': item.Total?.toLocaleString() || 0,
            ...(isSum && {'누적 총계': item.accumulatedTotal?.toLocaleString() || 0}),
            ...(type === 'ccu' && Object.fromEntries(CCU_LEVEL_GROUPS.map(({key, name}) => [name, item[key]])))
        }));
    }, [processedData, isSum, type]);

    const formatDate = (value) => {
        if (!value) return '';
        if (typeof value === 'number') {
            return dayjs(
                value.toString().length === 10 ? value * 1000 : value
            ).format('YYYY-MM-DD');
        }
        if (value instanceof Date) {
            return dayjs(value).format('YYYY-MM-DD');
        }
        // 문자열 등 기타 타입
        return dayjs(value).format('YYYY-MM-DD');
    };

    // 커스텀 라벨 컴포넌트
    const CustomLabel = (props) => {
        const {x, y, value, index} = props;

        // 데이터 포인트가 너무 많으면 일부만 표시
        const shouldShowLabel = processedData.length <= LIMIT_LABELS || index % Math.ceil(processedData.length / 8) === 0;

        if (!showLabels || !shouldShowLabel) return null;

        return (
            <text
                x={x}
                y={y - 10}
                fill={darkMode ? '#fff' : '#666'}
                textAnchor="middle"
                dy={-6}
                fontSize="11"
                fontWeight="500"
                className="chart-label"
            >
                {value?.toLocaleString()}
            </text>
        );
    };

    const CustomTooltip = ({active, payload, label}) => {
        if (active && payload && payload.length) {
            return (
                <div className={`chart-tooltip ${darkMode ? 'dark' : 'light'}`}>
                    <Text strong>{label}</Text>
                    <Divider style={{margin: '8px 0'}}/>
                    {/* ccu 는 영역 색이 옅어서 글자색 대신 색상 칩으로 구분 */}
                    {(isCcu ? [...payload].reverse() : payload).map((entry, index) => (
                        <div key={index} style={{color: entry.color, marginBottom: '4px'}}>
                            {isCcu && (
                                <span style={{display: 'inline-block', width: 10, height: 10, marginRight: 6, borderRadius: 2, background: entry.color}}/>
                            )}
                            <Text style={isCcu ? undefined : {color: entry.color}}>
                                {entry.name || entry.dataKey}: {Number(entry.value ?? 0).toLocaleString()}
                            </Text>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    return (
        <Card
            className={`chart-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            title={
                <Space align="center">
                    <div className="chart-icon">
                        <BarChartOutlined/>
                    </div>
                    <div>
                        <Title level={5} style={{margin: 0, color: 'var(--text-primary)'}}>
                            {title}
                        </Title>
                        <Text type="secondary" style={{fontSize: '12px'}}>
                            {dateRange && `${formatDate(dateRange.begin)} ~ ${formatDate(dateRange.end)}`}
                        </Text>
                    </div>
                </Space>
            }
            extra={
                <Space>
                    {/* 통화 선택 및 환율 표시: revenue 타입일 때만 노출 */}
                    {type === 'revenue' && (
                        <Space>
                            <Text type="secondary" style={{fontSize: '12px'}}>
                                {`1$ ≈ ${exchangeRate.toLocaleString()}₩`}
                            </Text>
                            <AntTooltip title="통화 선택">
                                <Switch
                                    checked={currency === 'KRW'}
                                    onChange={checked => setCurrency(checked ? 'KRW' : 'USD')}
                                    checkedChildren="원(₩)"
                                    unCheckedChildren="달러($)"
                                />
                            </AntTooltip>
                        </Space>
                    )}
                    {processedData.length > 0 && (
                        <Space>
                            <AntTooltip title={showLabels ? "수치 라벨 숨기기" : "수치 라벨 표시"}>
                                <Switch
                                    checked={showLabels}
                                    onChange={setShowLabels}
                                    checkedChildren={<EyeOutlined/>}
                                    unCheckedChildren={<EyeInvisibleOutlined/>}
                                />
                            </AntTooltip>
                            <AntTooltip title="데이터 새로고침">
                                <Button
                                    type="text"
                                    icon={<ReloadOutlined/>}
                                    onClick={refetch}
                                    loading={isLoading}
                                    size="small"
                                />
                            </AntTooltip>
                        </Space>
                    )}
                    {processedData.length > 0 && (
                        <CSVLink
                            data={csvData}
                            filename={`${type}_${dayjs().format('YYYYMMDDHHmmss')}.csv`}
                            style={{textDecoration: 'none'}}
                        >
                            <AntTooltip title="CSV 다운로드">
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined/>}
                                    size="small"
                                    className="download-btn"
                                >
                                    CSV
                                </Button>
                            </AntTooltip>
                        </CSVLink>
                    )}
                </Space>
            }
            style={{
                background: 'var(--card-background)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                boxShadow: 'var(--card-shadow)'
            }}
        >
            {processedData.length === 0 ? (
                <div className="chart-empty">
                    <BarChartOutlined style={{fontSize: '48px', color: '#ccc'}}/>
                    <Text type="secondary" style={{marginTop: 16, display: 'block'}}>
                        표시할 데이터가 없습니다
                    </Text>
                </div>
            ) : (
                <div className="chart-container">
                    <div className="chart-wrapper" style={{height: height || 400}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={processedData}
                                margin={{top: 40, right: 30, left: 20, bottom: 40}}
                            >
                                <defs>
                                    <linearGradient id="total" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="accumulated" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#52c41a" stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <XAxis
                                    dataKey="keyName"
                                    stroke={darkMode ? '#999' : '#666'}
                                    fontSize={12}
                                    tickLine={false}
                                />
                                <YAxis
                                    yAxisId="left"
                                    stroke="#1890ff"
                                    fontSize={12}
                                    tickLine={false}
                                    tickFormatter={(value) => value.toLocaleString()}
                                    domain={isCcu ? [0, 'auto'] : [0, Math.max(...processedData.map(d => d.Total), 10000)]}
                                />
                                {isSum && (
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        stroke="#52c41a"
                                        fontSize={12}
                                        tickLine={false}
                                        tickFormatter={(value) => value.toLocaleString()}
                                    />
                                )}
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={darkMode ? '#333' : '#f0f0f0'}
                                    opacity={0.7}
                                />
                                <Tooltip content={<CustomTooltip/>}/>
                                {isCcu && (
                                    <Legend layout="vertical" align="right" verticalAlign="top" content={renderCcuToggles}/>
                                )}
                                {isCcu && CCU_LEVEL_GROUPS.map(({key, name, color}) => (
                                    <Area
                                        key={key}
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey={key}
                                        name={name}
                                        stackId="level"
                                        stroke={color}
                                        fill={color}
                                        fillOpacity={0.9}
                                        dot={false}
                                        hide={hiddenKeys.includes(key)}
                                    />
                                ))}
                                <Area
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="Total"
                                    name="총계"
                                    stroke={totalColor}
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill={isCcu ? 'none' : 'url(#total)'}
                                    hide={hiddenKeys.includes('Total')}
                                    dot={false} // 도트 항상 숨김
                                    activeDot={{
                                        r: 6,
                                        stroke: totalColor,
                                        strokeWidth: 2,
                                        fill: totalColor
                                    }}
                                >
                                    <LabelList content={<CustomLabel/>}/>
                                </Area>
                                {isSum && (
                                    <Area
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="accumulatedTotal"
                                        name="누적 총계"
                                        stroke="#52c41a"
                                        strokeWidth={2}
                                        fill="url(#accumulated)"
                                        dot={false} // 도트 항상 숨김
                                        activeDot={{
                                            r: 6,
                                            stroke: '#52c41a',
                                            strokeWidth: 2,
                                            fill: '#52c41a'
                                        }}
                                    >
                                    </Area>
                                )}
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* 라벨 표시 안내 */}
                    {showLabels && processedData.length > LIMIT_LABELS && (
                        <div className="chart-notice">
                            <Text type="secondary" style={{fontSize: '11px'}}>
                                💡 데이터가 많아 일부 포인트의 수치만 표시됩니다.
                            </Text>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default CustomAreaChart;